import * as React from "react"
import { Check, ChevronsUpDown, Plus, AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

/**
 * Combobox with typeahead search
 * @param {Object} props
 * @param {Array} props.options - Array of { value, label, subtitle?, badge? }
 * @param {string} props.value - Current selected value
 * @param {function} props.onValueChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {string} props.emptyText - Text when no results found
 * @param {boolean} props.allowCreate - Show "create new" option when not found
 * @param {function} props.onCreateNew - Callback when "create new" is clicked
 * @param {string} props.createNewLabel - Label for create new button
 * @param {boolean} props.disabled - Disable the combobox
 * @param {string} props.className - Additional classes
 */
const Combobox = React.forwardRef(({
  options = [],
  value,
  onValueChange,
  placeholder = "Selecione...",
  searchPlaceholder = "Buscar...",
  emptyText = "Nenhum resultado encontrado.",
  allowCreate = false,
  onCreateNew,
  createNewLabel = "Criar novo item",
  disabled = false,
  className,
  "data-testid": dataTestId,
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")

  const selectedOption = options.find((opt) => opt.value === value)

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options
    const query = searchQuery.toLowerCase()
    return options.filter((opt) => 
      opt.label.toLowerCase().includes(query) ||
      (opt.subtitle && opt.subtitle.toLowerCase().includes(query))
    )
  }, [options, searchQuery])

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue === value ? "" : selectedValue)
    setOpen(false)
    setSearchQuery("")
  }

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew(searchQuery)
    }
    setOpen(false)
    setSearchQuery("")
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={dataTestId}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          {selectedOption ? (
            <span className="truncate">{selectedOption.label}</span>
          ) : (
            <span>{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={searchQuery}
            onValueChange={setSearchQuery}
            data-testid={dataTestId ? `${dataTestId}-search` : undefined}
          />
          <CommandList
            className="max-h-[240px] overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {filteredOptions.length === 0 && !allowCreate && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {filteredOptions.length === 0 && allowCreate && searchQuery && (
              <div className="p-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 px-2">
                  <AlertCircle className="h-4 w-4" />
                  <span>"{searchQuery}" não encontrado no cadastro</span>
                </div>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 border-dashed"
                  onClick={handleCreateNew}
                  data-testid={dataTestId ? `${dataTestId}-create-new` : undefined}
                >
                  <Plus className="h-4 w-4" />
                  {createNewLabel}: "{searchQuery}"
                </Button>
              </div>
            )}
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{option.label}</span>
                    {option.subtitle && (
                      <span className="text-xs text-muted-foreground truncate">
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                  {option.badge && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-muted">
                      {option.badge}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
})

Combobox.displayName = "Combobox"

export { Combobox }
